/**
 * FormTitle — compact on-page chip strip (BO UI Standard v3, 2026-09-18).
 * ---------------------------------------------------------------------------
 * The page TITLE lives in the TOP BAR (AppShell, next to the hamburger), so this
 * component no longer renders the big on-page title heading. It carries the
 * shared load-time chip + the view-only Audit chip, but — user request
 * 2026-09-18 — those now render at the FAR RIGHT of the shared <AppToolbar>,
 * next to "Export to", GLOBALLY on every form.
 *
 * So FormTitle PUBLISHES its load-time + audit to the shared FormMetaContext
 * (usePublishFormMeta), which <AppToolbar> consumes and renders. When a form has
 * NO toolbar, FormTitle renders the chips itself here (fallback) so the
 * load-time is never lost. Every form's existing `<FormTitle …/>` call keeps
 * working with NO per-form change; the old heading props (pageId / icon /
 * iconColor / title / subtitle) are accepted for compatibility and ignored.
 */
import { LoadTimeChip } from './LoadTimeChip';
import { AuditTrailButton } from './AuditTrailButton';
import { usePublishFormMeta, useHasToolbar } from './FormMetaContext';

interface Props {
  /** @deprecated title now renders in the top bar — kept for call compatibility. */
  pageId?: string;
  /** @deprecated */ icon?: string;
  /** @deprecated */ iconColor?: string;
  /** @deprecated */ title?: string;
  /** @deprecated */ subtitle?: string;
  /** Last fetch duration (ms) — the shared load-time chip. */
  loadMs?: number | null;
  /** True while fetching — the chip shows "Loading…". */
  loading?: boolean;
  /** Audit Trail (view-only) — config-gated violet chip; `auditFormName` matches
   *  dbo.AuditData.FormName, `auditSourceId` optionally narrows to one record. */
  auditFormName?: string;
  auditSourceId?: string;
}

export function FormTitle({ loadMs = null, loading, auditFormName, auditSourceId }: Props) {
  // Publish to the shared context so <AppToolbar> renders the chips next to
  // "Export to" (global, user request 2026-09-18).
  usePublishFormMeta({ loadMs, loading, auditFormName, auditSourceId });
  const hasToolbar = useHasToolbar();
  // A toolbar is present → the chips render THERE (next to Export). But we keep
  // an EMPTY .ft-chips placeholder here so the title-row DOM is UNCHANGED and no
  // sibling control shifts position: many forms put a button (e.g. Load) next to
  // <FormTitle> in a `justify-content: space-between` row and rely on this left
  // element to hold that button at the right (user request 2026-09-18: removing
  // the title must NOT move the Load button or any other control).
  if (hasToolbar) return <div className="ft-chips" aria-hidden="true" />;
  // No toolbar on this form → keep the chips here as a fallback (never lost).
  if (loadMs == null && !loading && !auditFormName) return null;
  return (
    <div className="ft-chips">
      <LoadTimeChip ms={loadMs} loading={loading} />
      {auditFormName && (
        <AuditTrailButton variant="chip" formName={auditFormName} sourceId={auditSourceId} />
      )}
    </div>
  );
}
